import BaseAuthProvider from './BaseAuthProvider'
import AuthService from '../service'
import { AuthProviders, OAuthBackends, SocialProviders } from '../constants/authProviders'
import { loadInitFacebookSDK } from '@/utils/social_oauth/facebook'

/**
 * Facebook OAuth Authentication Provider
 * Uses Facebook SDK v15.0
 */
export class FacebookAuthProvider extends BaseAuthProvider {
    constructor() {
        super()
        this._sdkLoaded = false
    }

    getMethod() {
        return AuthProviders.FACEBOOK
    }

    /**
     * Facebook login has custom restrictions (same as Google):
     * 1. Must be in .env providers
     * 2. Can be disabled by backend (appConfig.disable_social_login)
     * 3. Can be disabled for specific subdomains (e.g., 'photos')
     * 4. Requires Facebook App ID in environment
     */
    isEnabled(context) {
        // 1. Check .env (base requirement)
        if (!context.envProviders.includes(this.getMethod())) {
            return false
        }

        // 2. Check backend override (can only disable)
        if (context.appConfig && context.appConfig.disable_social_login) {
            return false
        }

        // 3. Check hardcoded subdomain rules
        // CUSTOM LOGIC: 'photos' subdomain disables all social login
        if (context.subdomain === 'photos') {
            return false
        }

        // 4. Check if email is prefilled from external source (preview JWT, etc.)
        // When email is prefilled, user must login with that specific email
        // Social login is disabled to prevent wrong account usage
        if (context.contextParams && context.contextParams.email) {
            return false
        }

        // 5. Check required credentials
        if (!process.env.VUE_APP_FACEBOOK_APP_ID) {
            console.warn('Facebook provider enabled but VUE_APP_FACEBOOK_APP_ID not configured')
            return false
        }

        return true
    }

    /**
     * Load Facebook SDK if not already loaded
     * @returns {Promise<void>}
     */
    async loadSDK() {
        if (this._sdkLoaded) return

        loadInitFacebookSDK()
        this._sdkLoaded = true

        // Wait for SDK to load
        await new Promise(resolve => setTimeout(resolve, 1000))
    }

    /**
     * Start Facebook OAuth login flow
     * @returns {Promise<string>} Facebook access token
     */
    async startLogin() {
        await this.loadSDK()

        const params = { scope: 'email' }

        return new Promise((resolve, reject) => {
            window.FB.login(response => {
                if (response && response.authResponse) {
                    // Verify user has email permission
                    window.FB.api(`/${response.authResponse.userID}?fields=email`, params, userResponse => {
                        if (userResponse && userResponse.email) {
                            resolve(response.authResponse.accessToken)
                        } else {
                            // Email permission denied, revoke app permissions
                            window.FB.api(`/${response.authResponse.userID}/permissions/`, 'delete', () => {
                                reject(new Error('Facebook email permission required'))
                            })
                        }
                    })
                } else {
                    reject(new Error('Facebook login cancelled'))
                }
            }, params)
        })
    }

    /**
     * Authenticate with Facebook OAuth token
     * @param {Object} credentials
     * @param {string} credentials.token - Facebook OAuth access token (optional, will prompt if not provided)
     * @param {boolean} credentials.allowRegister - Allow new user registration
     * @returns {Promise<{token: string, tokenType: string}>}
     */
    async authenticate({ token, allowRegister = false }) {
        // If no token provided, start OAuth flow
        if (!token) {
            token = await this.startLogin()
        }

        // Convert Facebook token to app token
        const response = await AuthService.convertAuthToken({
            backend: OAuthBackends[AuthProviders.FACEBOOK],
            token: token,
            grant_type: 'convert_token',
            client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
            allow_register: allowRegister
        })

        // Return token plus any additional metadata from backend
        return {
            token: response.data.access_token,
            tokenType: 'Bearer',
            ...response.data  // Include metadata if backend adds any in future
        }
    }

    /**
     * Suggest alternatives - other social providers or password
     */
    getAlternatives(enabledProviders) {
        const alternatives = []

        // Other social providers
        const otherSocial = SocialProviders.filter(p =>
            p !== this.getMethod() && enabledProviders.includes(p)
        )
        alternatives.push(...otherSocial)

        // Password login
        if (enabledProviders.includes(AuthProviders.PASSWORD)) {
            alternatives.push(AuthProviders.PASSWORD)
        }

        return alternatives
    }
}

export default FacebookAuthProvider
